import React, { useCallback, useRef, useState } from 'react';
import Select from 'react-select';
import { cloneDeep } from 'lodash';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { MultiSelect } from 'react-multi-select-component';
import { useParams } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import moment from 'moment';
import { useCareInstructionState } from '../CareLabelProvider/CareUserInstructionStateProvider';
import { customStyles, shortenandHoverString } from '../../../../Components/helper';
import Loader from '../../../../Components/Utility/Loader/Loader';
import ConfirmationPopup from '../../../../Components/ModalPopup/ConfirmationPopup';
import { addCareUserInstruction, addToLibrary, fetchCareInstructionProduct } from '../store/CARELABEL.action';
import CustomDatePicker from '../../../../Components/Utility/DatePicker/DatePicker';
import ProductCareImage from './ProductCareImage';
import ImageUploadModal from '../../../../Components/ModalPopup/ImageUploadModal';
import { getImagesByLocation, IMAGE_PICKER_EXTENSIONS } from '../../../AssetLibrary/Assets/store/asset.service';
import BomImageDropzone from '../../../../Components/ImgDropzone/BomImageDropzone';
import { toasterMsg } from '../../../../Components/Toaster/Toaster';
import CustomTooltip from '../../../../Components/Tooltip/CustomTooltip';


function CareInstructionTable({ isSupplierLoggedIn, CareAddEditAccess, CareDeleteAccess, handleDeleteMultiple, isProductArchived }) {
  const { t } = useTranslation('common');
  const childRef = useRef();

  const { CareLabelstate, setCareLabelState, setSaveClicked, saveClicked, userState, handleRowSelection, handleSelectAll, setUserState } =
    useCareInstructionState();
  const [imageRowIndex, setImageRowIndex] = useState('');
  const [codeColumn, setCodeColumn] = useState('');
  const [rowDetail, setRowDetail] = useState({});
  const [showLoad, setShowLoad] = useState(false);
  const { CareLabelList } = CareLabelstate;
  const findIndex = CareLabelList?.findIndex((item) => item?.tabId === userState?.tabId) || 0;
  const tabName = CareLabelList[findIndex]?.tabName;
  const param = useParams();
  const [state, setState] = useState({
    imageUpload: false,
    uploadIndex: -1,
    fullImage: null,
    deleteImage: false,
    addLibraryModel: false,
    code: '',
    elementData: '',
  });
  const dispatch = useDispatch();

  const fetchCareImages = useCallback(
    (search, page, limit, allowedImageExtensions) =>
      getImagesByLocation('Care/User Instructions', param?.devOrderId, search, allowedImageExtensions, page, limit),
    [param?.devOrderId],
  );

  const handleRefrenceCode = (e) => {
    setState((prev) => ({
      ...prev,
      code: e?.target?.value,
    }));
  };

  const closeAddUpdateLibrary = () => {
    setState((prevState) => ({
      ...prevState,
      addLibraryModel: false,
    }));
  };

  const onConfirmAddUpdateLibrary = async () => {
    const obj = {
      columnId: codeColumn,
      value: state?.code,
      rowDetail,
      tabId: userState.tabId,
    };
    const res = await dispatch(addToLibrary(param?.devOrderId, obj, t));
    if (res) {
      closeAddUpdateLibrary();
    }
  };
  const handleImageUpload = (rowIndex, columnId, value, dataRowIndex) => {
    setUserState((prevState) => ({
      ...prevState,
      showImageUpload: true,
      selectedImageRowIndex: rowIndex,
      selectedImageRowColumnId: columnId,
      rowImage: value,
    }));
    setImageRowIndex(dataRowIndex);
  };

  const handleImageDelete = (rowIndex, columnId, value, dataRowIndex) => {
    setUserState((prevState) => ({
      ...prevState,
      showImgDeleteModal: true,
      selectedImageRowIndex: rowIndex,
      selectedImageRowColumnId: columnId,
      rowImage: null,
    }));
    setImageRowIndex(dataRowIndex);
  };
  const closeImgDeleteModal = () => {
    setUserState((prevState) => ({
      ...prevState,
      showImgDeleteModal: false,
      selectedImageRowIndex: '',
      selectedImageRowColumnId: '',
      rowImage: null,
    }));
  };

  const deleteImgConfirmed = () => {
    const copyTabData = cloneDeep(CareLabelList?.[findIndex]?.tabDetails);
    copyTabData?.forEach((element) => {
      if (element?.columnId === userState?.selectedImageRowColumnId && (imageRowIndex === element?.rowIndex || userState?.editingRowId === element?.rowId)) {
        element.value = { image: '', fullImage: '' };
      }
    });
    const updatedCareLabelList = CareLabelstate.CareLabelList.map((item, index) => {
      if (index === findIndex) {
        return {
          ...item,
          tabDetails: copyTabData, // Replace with filtered data
        };
      }
      return item;
    });

    setCareLabelState((prevState) => ({
      ...prevState,
      CareLabelList: updatedCareLabelList,
    }));
    setUserState((prevState) => ({
      ...prevState,
      showImgDeleteModal: false,
      selectedImageRowIndex: '',
      selectedImageRowColumnId: '',
    }));
  };

  const handleEditClick = (rowId) => {
    setUserState((prevState) => ({
      ...prevState,
      editingRowId: rowId,
      editMode: true,
    }));
  };

  const handleAddtoLibrary = (rowDetails) => {
    setRowDetail(rowDetails);
    const codeId = CareLabelList[findIndex].tabColumns?.find((col) => col.name === 'Code')?._id;
    const code = rowDetails.find((ele) => ele.columnId === codeId)?.value;
    setCodeColumn(codeId);
    setState((prev) => ({
      ...prev,
      addLibraryModel: true,
      code,
    }));
  };

  const validateFields = (row) => {
    const mandatoryColumns = CareLabelList[findIndex].tabColumns?.filter((column) => {
      return column?.columnDetails?.columnType?.key !== 'Status' && column?.columnDetails?.isRequired;
    });
    let error = true;
    const requiredColumns = mandatoryColumns?.map((e) => e._id);
    const hasValue = requiredColumns?.every((element) => {
      return !!row?.find((e) => e?.columnId === element);
    });
    if (!hasValue) {
      error = false;
    } else {
      row?.forEach((element) => {
        if (requiredColumns?.includes(element?.columnId)) {
          if (element?.value) {
            const mandatoryColumnTypes = mandatoryColumns?.find((e) => e._id === element?.columnId)?.columnDetails;
            if (mandatoryColumnTypes?.columnType?.key === 'Textbox' && element?.value?.length > mandatoryColumnTypes?.characterLimit) {
              error = false;
            }
          } else {
            error = false;
          }
        }
      });
    }
    return error;
  };
  const handleSaveClick = async (row) => {
    setSaveClicked(true);
    if (validateFields(row)) {
      setShowLoad(true);
      const payload = {
        devOrderId: param?.devOrderId,
        tabId: CareLabelstate?.CareLabelList?.[findIndex]?.tabId,
        rowDetails: row,
        isAddToAssetLibrary: userState?.addToAssetLibrary === true,
      };
      const res = await dispatch(addCareUserInstruction(payload, t));
      if (res?.code === 200) {
        await dispatch(fetchCareInstructionProduct(param));
        setUserState((prevState) => ({
          ...prevState,
          editingRowId: '',
          addNewRow: false,
          editMode: false,
          tabId: CareLabelstate?.CareLabelList?.[findIndex]?.tabId,
        }));
        setSaveClicked(false); // check this after fixing backend issue of edit
        setShowLoad(false);
      }
    }
  };
  const handleCancelClick = async (row) => {
    setShowLoad(false);
    setSaveClicked(false);

    if (!row[0]?.rowId) {
      // Clone tabDetails and filter out entries without rowId
      const copysTabData = cloneDeep(CareLabelstate?.CareLabelList?.[findIndex]?.tabDetails);
      const filteredData = copysTabData?.filter((item) => item.rowId);

      // Create an updated CareLabelList with filtered tabDetails
      const updatedCareLabelList = CareLabelstate.CareLabelList.map((item, index) => {
        if (index === findIndex) {
          return {
            ...item,
            tabDetails: filteredData, // Replace with filtered data
          };
        }
        return item;
      });

      // Update state
      setCareLabelState((prevState) => ({
        ...prevState,
        CareLabelList: updatedCareLabelList,
      }));

      setUserState((prevState) => ({
        ...prevState,
        editingRowId: '',
        addNewRow: false,
      }));
    } else {
      setUserState((prevState) => ({
        ...prevState,
        editingRowId: '',
        addNewRow: false,
      }));
    }
  };

  const handleChange = (value, rowId, columnId, fromDate) => {
    const copyTabData = cloneDeep(CareLabelstate?.CareLabelList?.[findIndex]?.tabDetails);
    const foundCell = copyTabData.find((cell) => cell?.rowId === rowId && cell?.columnId === columnId);
    const foundRow = copyTabData.find((cell) => cell?.rowId === rowId);
    if (!foundCell) {
      copyTabData?.push({ columnId, value, rowId: foundRow.rowId, rowIndex: foundRow.rowIndex, tabId: foundRow.tabId });
    } else {
      copyTabData?.forEach((element) => {
        if (element?.rowId === rowId && element?.columnId === columnId) {
          element.value = value;
          if (fromDate) {
            element.isDate = true;
          }
        }
      });
    }
    const updatedCareLabelList = CareLabelstate.CareLabelList.map((item, index) => {
      if (index === findIndex) {
        return {
          ...item,
          tabDetails: copyTabData, // Push the new tabDetails
        };
      }
      return item;
    });
    setCareLabelState((prevState) => ({
      ...prevState,
      CareLabelList: updatedCareLabelList,
    }));
  };

  const handleCareImageSave = ({ file, assetKey, assetId, addToAssetLibrary }) => {
    /**
     * Held on the row until Save, because the picture is applied to the cell here but only sent
     * when handleSaveClick posts the row. Never set for a library pick, which is already an asset.
     */
    setUserState((prevState) => ({ ...prevState, addToAssetLibrary: !assetKey && addToAssetLibrary === true }));
    const applyImage = (imageValue, selectedAssetId) => {
      const copyTabData = cloneDeep(CareLabelList?.[findIndex]?.tabDetails);
      copyTabData?.forEach((element) => {
        if (element?.columnId === userState?.selectedImageRowColumnId && (imageRowIndex === element?.rowIndex || userState?.editingRowId === element?.rowId)) {
          element.value = { image: imageValue, fullImage: imageValue, assetId: selectedAssetId || null };
        }
      });
      const updatedCareLabelList = CareLabelstate.CareLabelList.map((item, index) => {
        if (index === findIndex) {
          return { ...item, tabDetails: copyTabData };
        }
        return item;
      });
      setCareLabelState((prevState) => ({
        ...prevState,
        CareLabelList: updatedCareLabelList,
      }));
      setUserState((prevState) => ({
        ...prevState,
        showImageUpload: false,
        selectedImageRowIndex: '',
        selectedImageRowColumnId: '',
        rowImage: null,
      }));
    };

    if (assetKey) {
      applyImage(assetKey, assetId);
      return;
    }
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        if (reader.result) applyImage(String(reader.result), null);
      };
    }
  };

  const eleRender = (column, value, rowIndex, dataRowIndex, rowId) => {
    switch (column?.columnDetails?.columnType?.key) {
      case 'Image':
        return (
          <ProductCareImage
            dataValue={value}
            handleImageUpload={() => handleImageUpload(rowIndex, column._id, value, dataRowIndex)}
            handleImageDelete={() => handleImageDelete(rowIndex, column._id, value, dataRowIndex)}
            t={t}
          />
        );
      case 'Dropdown':
        return (
          <div className="common-react-select mb-0">
            <Select
              className="common-select"
              classNamePrefix="common-select"
              options={column?.columnDetails?.options}
              styles={customStyles}
              placeholder={t('CARE_LABELS.TABLE.SYMBOL_TABLE.SELECT_CARE_TYPE')}
              menuPosition="fixed"
              getOptionLabel={(option) => `${option?.name}`}
              getOptionValue={(option) => `${option?.name}`}
              name="careType"
              value={value || null}
              onChange={(selectedOption) => handleChange(selectedOption, rowId, column?._id, false)}
            />
          </div>
        );
      case 'Multi_select_Dropdown': {
        const options = column?.columnDetails?.options?.map((option) => ({
          label: option?.name,
          value: option?.name,
        }));
        return (
          <MultiSelect
            className="product-custom-multiselect common-new-multiselect"
            options={options || []}
            placeholder={t('CARE_LABELS.TABLE.SYMBOL_TABLE.SELECT_CARE_TYPE')}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.value}
            menuPosition="fixed"
            onChange={(selectedOption) => handleChange(selectedOption, rowId, column?._id, false)}
            value={value || []}
          />
        );
      }
      case 'Status':
        return (
          <div className="background-td-color w-100" style={{ backgroundColor: column?.columnDetails?.status?.colourHexCode }}>
            <span>{column?.columnDetails?.status?.colourName}</span>
          </div>
        );
      case 'Radio_button':
        return (
          <div className="d-flex flex-wrap align-items-center">
            {column?.columnDetails?.options?.map((option, i) => {
              const isChecked = value?.name === option?.name;
              return (
                <p className="custom-radio mb-0  mr-2" key={`${rowId}_${column?._id}_${i}`}>
                  <input
                    type="radio"
                    id={`radio_${rowId}_${column?._id}_${i}`}
                    name={`radio_${rowId}_${column?._id}_${i}`}
                    checked={isChecked}
                    onChange={() => handleChange(option, rowId, column?._id, false)}
                  />
                  <label htmlFor={`radio_${rowId}_${column?._id}_${i}`} className="radio f-14 text-black">
                    {option.name}
                  </label>
                </p>
              );
            })}
          </div>
        );
      case 'Date_picker':
        return (
          <CustomDatePicker
            keyProp={`date_${rowIndex}_${column?._id}`}
            dateFormat="dd-MMM-yyyy"
            valueProp={value || null}
            placeholder={t('USER_INSTRUCTION_LIBRARY.USER_INSTRUCTION.DATE_PLACEHOLDER')}
            onChange={(e) => handleChange(e, rowId, column?._id, true)}
          />
        );
      case 'Textbox':
        return (
          <div className="common-input-control mb-0">
            <input
              type="text"
              maxLength={column?.columnDetails?.characterLimit}
              value={value || ''}
              onChange={(e) => handleChange(e?.target?.value, rowId, column?._id, false)}
              className="form-control text-bordered"
            />
            <span className="character-show">
              {value?.length || 0}/{column?.columnDetails?.characterLimit}
            </span>
          </div>
        );
      default:
        return (
          <div className="common-input-control mb-0">
            <input
              type="text"
              value={value || ''}
              onChange={(e) => handleChange(e?.target?.value, rowId, column?._id, false)}
              className="form-control  text-bordered"
            />
          </div>
        );
    }
  };
  const renderValue = (column, value, rowId, rowIndex, dataRowIndex) => {
    if (userState?.editingRowId === rowId || (userState?.addNewRow && !rowId)) {
      return eleRender(column, value, rowIndex, dataRowIndex, rowId);
    }
    switch (column?.columnDetails?.columnType?.key) {
      case 'Status':
        return (
          <div className="background-td-color w-100" style={{ backgroundColor: column?.columnDetails?.status?.colourHexCode }}>
            <span>{column?.columnDetails?.status?.colourName}</span>
          </div>
        );
      case 'Radio_button':
      case 'Dropdown':
        return value?.name || '';
      case 'Image':
        return <ProductCareImage dataValue={value} isPageDisable t={t} />;
      case 'Multi_select_Dropdown':
        return value?.map((val) => val?.label).join(', ') || '';
      case 'Date_picker':
        return (value && moment(value).format('DD-MM-YYYY')) || '';
      default:
        if (!value || typeof value === 'object') {
          return '';
        }
        if (typeof value === 'string' && value.length > 80) {
          return shortenandHoverString(value, 80, 'right');
        }
        return value;
    }
  };

  const stickyClass = (colIndex, tab) => {
    if (tab === 'Symbols' && colIndex === 1) {
      return 'first-fix-col';
    }
    if (tab !== 'Symbols' && colIndex === 0) {
      return 'first-fix-col';
    }
    return '';
  };
  const checkColour = (element, value) => {
    switch (element?.colourValue?._id) {
      case 'greater_than':
        return parseFloat(element?.startPercentage) <= parseFloat(value) && element?.colourType;
      case 'between':
        return parseFloat(element?.startPercentage) <= parseFloat(value) && parseFloat(element?.endPercentage) >= parseFloat(value) && element?.colourType;
      case 'less_than':
        return parseFloat(element?.startPercentage) >= parseFloat(value) && element?.colourType;
      case 'equal':
        return parseFloat(element?.startPercentage) === parseFloat(value) && element?.colourType;
      default:
        return '';
    }
  };
  const checkForColour = (column, value) => {
    let sourceColour = '';
    if (['Number_Decimal', 'Number_percentage'].includes(column?.columnDetails?.columnType?.key) && column?.columnDetails?.colourArray?.length > 0) {
      sourceColour = 'cell-grey';
      column?.columnDetails?.colourArray?.forEach((element) => {
        if (value) {
          const colourName = checkColour(element, parseFloat(value));
          if (colourName === 'green') {
            sourceColour = 'cell-green';
          } else if (colourName === 'red') {
            sourceColour = 'cell-red';
          } else if (colourName === 'amber') {
            sourceColour = 'cell-amber';
          }
        }
      });
      return sourceColour;
    }
  };

  const baseColSpan = CareLabelList?.[findIndex]?.tabColumns?.length || 0;
  let colSpanData;

  if (CareDeleteAccess) {
    if (isSupplierLoggedIn) {
      colSpanData = baseColSpan + 1;
    } else {
      colSpanData = baseColSpan + 2;
    }
  } else {
    colSpanData = baseColSpan + 1;
  }

  return (
    <div>
      {CareLabelstate?.pageLoader ? (
        <Loader />
      ) : (
        <div style={{ minHeight: '430px' }} className="table-responsive mt-24">
          <table id="care-user-table" className="table mb-0 common-custom-table layout-fixed">
            <thead>
              <tr>
                {!isSupplierLoggedIn && (CareAddEditAccess || CareDeleteAccess) && (
                  <th aria-label="check" className="w-80px">
                    <div className="common-checkbox no-label margin-auto">
                      <input
                        type="checkbox"
                        name="check_all_symbol"
                        id="check_all_symbol"
                        onChange={handleSelectAll}
                        checked={userState?.isSelectAll}
                        disabled={isProductArchived}
                      />
                      <label htmlFor="check_all_symbol" className="chklabel">
                        <span />
                      </label>
                    </div>
                  </th>
                )}
                {CareLabelList?.[findIndex]?.tabColumns
                  ?.sort((a, b) => a.sequence - b.sequence) // Sort the columns based on the sequence/order
                  .map((column, columnIndex) => (
                    <th
                      key={column?._id}
                      className={`w-${column?.name === 'image' ? '100px' : '150px'} ${stickyClass(columnIndex, CareLabelList[findIndex]?.tabName)}`}
                    >
                      {column?.name}
                    </th>
                  ))}
                <th aria-label="Expand/Collapse" className="w-100px last-fix-col" />
              </tr>
            </thead>
            <tbody>
              {CareLabelList?.[findIndex]?.tabDetails?.length > 0 ? (
                [...new Set(CareLabelList?.[findIndex]?.tabDetails?.sort((a, b) => a.sequence - b.sequence).map((detail) => detail.rowId))].map(
                  (row, rowIndex) => {
                    const rowDetails = CareLabelList?.[findIndex]?.tabDetails?.filter((detail) => detail.rowId === row);
                    return (
                      <tr className="multi-options" key={row}>
                        {!isSupplierLoggedIn && (CareAddEditAccess || CareDeleteAccess) && (
                          <td>
                            <div className="common-checkbox no-label margin-auto">
                              <input
                                type="checkbox"
                                name={`symbol_${row}`}
                                id={`symbol_${row}`}
                                checked={userState?.selectedIdArray?.includes(row)}
                                onChange={() => handleRowSelection(row)}
                                disabled={isProductArchived}
                              />
                              <label htmlFor={`symbol_${row}`} className="chklabel">
                                <span />
                              </label>
                            </div>
                          </td>
                        )}

                        {CareLabelList[findIndex]?.tabColumns
                          ?.sort((a, b) => a.sequence - b.sequence)
                          .map((column, columnIndex) => {
                            const data = rowDetails?.find((detail) => detail.columnId === column?._id);
                            return (
                              <td
                                key={column?._id}
                                className={`${stickyClass(columnIndex, CareLabelList[findIndex]?.tabName)} ${checkForColour(column, data?.value)}`}
                              >
                                {renderValue(column, data?.value, row, rowIndex, data?.rowIndex)}
                                {saveClicked &&
                                  (!row || row === userState?.editingRowId) &&
                                  column?.columnDetails?.isRequired &&
                                  !data?.value &&
                                  column?.columnDetails?.columnType?.key !== 'Status' && (
                                    <span className="error-message mt-2">{`${column?.name} ${t(
                                      'USER_INSTRUCTION_LIBRARY.USER_INSTRUCTION.REQUIRED_FIELD',
                                    )}`}</span>
                                  )}
                              </td>
                            );
                          })}
                        <td className="last-fix-col text-right">
                          {userState?.editingRowId === row || (userState?.addNewRow && !row) ? (
                            <div className="d-flex supporting-action justify-content-end">
                              {!isSupplierLoggedIn && (CareAddEditAccess || CareDeleteAccess) && (
                                <>
                                  <CustomTooltip placement="top" tooltipText={t('COMMON_LABEL.SAVE')}>
                                    <button
                                      title={t('COMMON_LABEL.SAVE')}
                                      aria-label="button"
                                      type="button"
                                      className="btn p-0"
                                      onClick={() => handleSaveClick(rowDetails)}
                                      disabled={showLoad}
                                    >
                                      <i className="icon-Save-1 f-22" />
                                    </button>
                                  </CustomTooltip>
                                  <CustomTooltip placement="top" tooltipText={t('COMMON_LABEL.CANCEL')}>
                                    <button aria-label="button" type="button" className="btn p-0" onClick={() => handleCancelClick(rowDetails)}>
                                      <i className="icon-close f-16" />
                                    </button>
                                  </CustomTooltip>
                                </>
                              )}
                            </div>
                          ) : (
                            <div className="d-flex supporting-action justify-content-end">
                              {!isSupplierLoggedIn && (CareAddEditAccess || CareDeleteAccess) && (
                                <>
                                  <CustomTooltip placement="top" tooltipText={t('CARE_LABELS.TABLE.VIEW_ACTION.EDIT')}>
                                    <button
                                      aria-label="button"
                                      type="button"
                                      className="btn p-0"
                                      disabled={userState?.editingRowId === row || userState?.addNewRow || !row || isProductArchived}
                                      onClick={() => handleEditClick(row)}
                                    >
                                      <i className="icon-Edit-1 f-20" />
                                    </button>
                                  </CustomTooltip>
                                  <CustomTooltip placement="top" tooltipText={t('CARE_LABELS.TABLE.VIEW_ACTION.ADD_TO_LIBRARY')}>
                                    <button
                                      aria-label="button"
                                      type="button"
                                      className="btn p-0"
                                      disabled={userState?.editingRowId === row || userState?.addNewRow || !row || isProductArchived}
                                      onClick={() => handleAddtoLibrary(rowDetails)}
                                    >
                                      <i className="icon-Library f-20" />
                                    </button>
                                  </CustomTooltip>
                                  <CustomTooltip placement="top" tooltipText={t('CARE_LABELS.TABLE.VIEW_ACTION.DELETE')}>
                                    <button
                                      aria-label="button"
                                      type="button"
                                      className="btn p-0"
                                      onClick={() => handleDeleteMultiple(row)}
                                      disabled={isProductArchived}
                                    >
                                      <i className="icon-Delete f-20" />
                                    </button>
                                  </CustomTooltip>
                                </>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  },
                )
              ) : (
                <tr>
                  <td colSpan={colSpanData}>
                    <p className="text-center m-0">
                      {`${t('USER_INSTRUCTION_LIBRARY.USER_INSTRUCTION.NO')} ${
                        CareLabelList?.[findIndex]?.tabName ? CareLabelList?.[findIndex]?.tabName : ''
                      } ${t('USER_INSTRUCTION_LIBRARY.USER_INSTRUCTION.NO_LIBRARY_ENTRY_FOUND')}`}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <ImageUploadModal
            show={!!userState?.showImageUpload}
            fetchImages={fetchCareImages}
            allowedImageExtensions={IMAGE_PICKER_EXTENSIONS}
            showAddToAssetLibrary
            existingImage={userState?.rowImage?.image || null}
            onClose={() =>
              setUserState((prevState) => ({
                ...prevState,
                showImageUpload: false,
                selectedImageRowIndex: '',
                selectedImageRowColumnId: '',
                rowImage: null,
              }))
            }
            onSave={handleCareImageSave}
          />
          {userState?.showImgDeleteModal && (
            <ConfirmationPopup
              onOpen={userState?.showImgDeleteModal}
              onConfirm={() => deleteImgConfirmed()}
              title={t('USER_INSTRUCTION_LIBRARY.USER_INSTRUCTION.IMAGE_DELETE_TITLE')}
              message={t('USER_INSTRUCTION_LIBRARY.USER_INSTRUCTION.IMAGE_DELETE_MESSAGE')}
              toggle={closeImgDeleteModal}
              showThirdMsg
            />
          )}
          {state.addLibraryModel && (
            <ConfirmationPopup
              onOpen={state.addLibraryModel}
              onConfirm={() => onConfirmAddUpdateLibrary()}
              title={`${t('TAKING_DATES.ADD_UPDATE_NEW_LIB')} ${tabName} ${t('TAKING_DATES.INTO_CARE_LIBRARY')}`}
              code={state.code}
              isReferenceCode
              toggle={() => closeAddUpdateLibrary()}
              handleRefrenceCode={handleRefrenceCode}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default CareInstructionTable;
